import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useStaffForm } from "../../hooks/staff/use-staff-form";
import { useCreateStaff } from "@/hooks/staff/use-staff";
import { getStaffFormValues } from "@/default-form-values/staff/default-staff-form-values";
import {
  STAFF_DIALOG_WIDTH,
  STAFF_DIALOG_HEIGHT,
} from "@/constants/staff/staff-constants";
import { Separator } from "@/components/ui/separator";
import { StaffInfoStep } from "./add-staff-steps/staff-info-step";
import { StaffHoursStep } from "./add-staff-steps/staff-hours-step";
import { StaffServicesStep } from "./add-staff-steps/staff-services-step";
import { StaffDialogProgress } from "./add-staff-steps/staff-dialog-progress";
import { staffFormSchema } from "@/zod-schemas/staff/add-staff-schema";
import { toStaffRequest } from "@/mappers/staff/staff-form-to-request";

export function AddStaff() {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);

  const createStaffMutation = useCreateStaff();

  const form = useStaffForm({
    defaultValues: getStaffFormValues(),
    validators: {
      onChange: staffFormSchema,
      onSubmit: staffFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const createdStaff = await createStaffMutation.mutateAsync(
          toStaffRequest(value),
        );

        toast.success(
          `Staff member "${createdStaff.first_name} ${createdStaff.last_name}" created successfully`,
        );
        form.reset();
        setCurrentStep(0);
        setOpen(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        const errorDetail = error?.response?.data?.detail || "";

        if (statusCode === 409) {
          setServerError(errorDetail);
          setCurrentStep(0);
        } else {
          toast.error(
            `Failed to create staff member "${value.first_name} ${value.last_name}"`,
          );
          setCurrentStep(0);
          setOpen(false);
        }
      }
    },
  });

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setCurrentStep(0);
      setServerError(null);
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          Add Staff
        </Button>
      </DialogTrigger>
      <DialogContent
        className={`${STAFF_DIALOG_WIDTH} ${STAFF_DIALOG_HEIGHT} overflow-y-auto`}
      >
        <DialogHeader>
          <DialogTitle>New Staff</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <Separator />
          <StaffDialogProgress currentStep={currentStep} />

          <div className="mt-6">
            {currentStep === 0 && (
              <StaffInfoStep
                form={form}
                onNext={handleNext}
                onCancel={() => handleOpenChange(false)}
                serverError={serverError}
                setServerError={setServerError}
              />
            )}
            {currentStep === 1 && (
              <StaffHoursStep
                form={form}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {currentStep === 2 && (
              <StaffServicesStep form={form} onBack={handleBack} />
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
